import type { ReactNode } from 'react'

import { usePage } from '@inertiajs/react'
import OMSLogo from 'images/logo.svg?react'
import { PrimaryNavbar } from 'shared/Navbar'
import { Profile } from 'shared/Profile'
import { colors } from 'theme'

import { type GlobalPageProps } from 'helpers/interfaces'

import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import MuiLink from '@mui/material/Link'
import type { Theme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

import { PageMobileMenu } from './PageMobileMenu'
import { ADMIN_BANNER_HEIGHT, PAGE_HEADER_HEIGHT } from './consts'

type PageHeaderProps = {
  hideProfile?: boolean
  actions?: ReactNode
}

export const PageHeader = ({ hideProfile = false, actions }: PageHeaderProps) => {
  const { props } = usePage<GlobalPageProps>()
  const user = props.auth?.user ?? null
  const { adminBannerText } = props
  // noSsr reads the screen size straight away, so phones don't first render the full navigation
  const mobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'), { noSsr: true })

  return (
    <>
      {adminBannerText && (
        <Box
          role="region"
          aria-label="Admin banner"
          sx={(theme) => ({
            position: 'sticky',
            top: 0,
            zIndex: theme.zIndex.appBar + 1,
            height: ADMIN_BANNER_HEIGHT,
            lineHeight: `${ADMIN_BANNER_HEIGHT}px`,
            bgcolor: 'error.main',
            color: 'error.contrastText',
            textAlign: 'center',
            fontSize: '16px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          })}
        >
          {adminBannerText}
        </Box>
      )}
      <AppBar
        position="sticky"
        variant="header"
        sx={{ height: PAGE_HEADER_HEIGHT, top: adminBannerText ? ADMIN_BANNER_HEIGHT : 0 }}
      >
        <Container
          maxWidth="xl"
          disableGutters // width controls gutters
          sx={(_theme) => ({
            width: 'calc(100% - 20px)', // Matching Rails gutters
            height: PAGE_HEADER_HEIGHT,
            borderBottom: `1px solid ${colors.headerAppBarBorder}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          })}
        >
          <Box
            sx={(theme) => ({
              display: 'flex',
              height: '60px',
              [theme.breakpoints.down('sm')]: {
                height: '46px', // Magic numbers for mobile to match existing design
              },
            })}
          >
            <MuiLink
              href="/"
              aria-label="Oxford Medical Simulation"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                lineHeight: '0',
                '& svg': {
                  height: '100%',
                  width: 'auto',
                  display: 'block',
                },
              }}
            >
              {' '}
              <OMSLogo />
            </MuiLink>
          </Box>
          {user !== null && mobile && <PageMobileMenu user={user} />}
          {user !== null && !mobile && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                height: '100%',
              }}
            >
              {!hideProfile && <Profile user={user} />}
              {actions}
              <PrimaryNavbar user={user} />
            </Box>
          )}
        </Container>
      </AppBar>
    </>
  )
}
